/**
 * 執行期設定解析 —— 從 `public/config.json` 取值（單一來源，host + Cocos 共用）。
 *
 * 取得時機（2026-07 改版，**不再用同步 XHR**）：
 *   1. index.html 於 <head> 最前面以 inline <script> **非同步** fetch `/config.json`
 *      （與 entry bundle 下載並行，不多花 round trip；帶 8s AbortController timeout），
 *      結果放 `window.__APP_CONFIG__`、promise 放 `window.__APP_CONFIG_PROMISE__`。
 *   2. `main.ts` 在 `createApp().mount()` **之前** `await initRuntimeConfig()`。
 *
 * 🛑 因此本檔的值是 **`export let`（ESM live binding）**，不是 `const`：
 *    module 初始化當下還是空字串，`initRuntimeConfig()` 之後才有值。
 *    consumer 端寫法不變（`import { API_BASE }` 照用），但**不可以在 module scope
 *    把它快照成自己的 const**（`const X = API_BASE`）—— 那會凍在初始化前的空字串。
 *    要嘛在 function 內讀，要嘛用 `export { X as Y }` 這種 live re-export。
 *    per-game `content.ts` / `assets.ts` 是 lazy chunk（mount 後才 import），module scope
 *    拼字串安全。Cocos（iframe）另自行 fetch 同一份 config.json。
 *
 * 🛑 **config.json 為唯一來源、無 env fallback**（極簡）：window 缺值時 base = 空字串。
 *    → `public/config.json` 必須隨 build 部署、且 prod 由運維覆蓋；漏部署 = base 空 = app 壞。
 *
 * ☑️ **候選 domain race（2026-09-02 上線）**：`api` / `images` 兩個候選 array 各自 race，
 *    在 `mount()` **之前**決勝負，逾時 / 全掛 → fallback `[0]`（= 原本行為，不會更差）。
 *    兩者**並行**，所以開機最多只多 `RACE_TIMEOUT_MS`（2s），不是兩倍。
 *
 *    - **API**：`GET {base}/healthz` 且 **status === 204** 才算贏（免 token、無 body、
 *      不需 gameCode —— 2026-09-07 後端專為 race 開的輕量端點）。
 *      ⚠️ 後端目前只對部分遊戲開這支，沒開的款所有候選都會失敗 → fallback `[0]`（可接受）。
 *    - **圖片**：`HEAD` + `mode:'no-cors'`，**只看主機有沒有回應**（403 / 404 都算活著）。
 *      這樣就不需要知道任何圖片路徑（路徑要等 `/v1/images/external`，boot 時還沒有），
 *      也不需要對方開 CORS —— opaque 回應本來就讀不到內容，我們也不需要讀。
 *      比 `Image()` onload 好：那個一樣要有真實圖片 URL 才能測。
 *
 *    為什麼 race 完不用重構：`buildImageURL(img, imgBase = IMAGES_BASE)` 的預設參數是
 *    **呼叫時**求值、`imgFromCloud()` 也是在函式內讀，而 per-game `assets.ts` 是 mount 後
 *    才載的 lazy chunk → race 在 mount 前結束，兩條路都吃得到 raced 值。
 */

/**
 * 執行環境標記全集（`config.json.env` 的合法值）。取代舊 build-time `VITE_MODE`。
 * - local   本機 `pnpm dev`
 * - dev/stg/rel/sandbox/prod  各部署環境
 * - demo    試玩部署（guest-only，見 `IS_GUEST_LOGIN`）
 */
export const AppEnv = {
  Local: 'local',
  Dev: 'dev',
  Stg: 'stg',
  Rel: 'rel',
  Sandbox: 'sandbox',
  Prod: 'prod',
  Demo: 'demo',
} as const

export type AppEnv = (typeof AppEnv)[keyof typeof AppEnv]

interface AppConfig {
  /** 執行環境標記（見 {@link AppEnv}）。取代舊 build-time `VITE_MODE`。 */
  env?: string
  api?: string[]
  images?: string[]
  /** 新 loading 流程的開始鈕開關（kill switch）；缺值 = true。見 {@link LOADING_START_BUTTON} */
  loadingStartButton?: boolean
}

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig
    /** index.html inline script 發出的 config fetch（已自行 catch，不會 reject）。 */
    __APP_CONFIG_PROMISE__?: Promise<void>
  }
}

/** 去掉尾端斜線，避免拼路徑出現 //。 */
const strip = (raw: string | undefined): string => {
  const s = (raw ?? '').trim()
  return s.endsWith('/') ? s.slice(0, -1) : s
}

/** 取候選 array 的 [0]（無 env fallback；缺則空字串）。race 失敗時的 fallback 也是它。 */
const pick = (arr: string[] | undefined): string => strip(arr?.[0])

/** 候選 domain race 的逾時（ms）。逾時 → fallback `[0]`。會 block 開機，別調大。 */
export const RACE_TIMEOUT_MS = 2000

/**
 * `Promise.any` 的等價實作：**第一個成功**就 resolve，**全部失敗**才 reject。
 * 🛑 不直接用 `Promise.any` —— 它要 `lib: es2021`，本專案 target 較低，`vue-tsc -b` 會擋
 *    （TS2550）。為了一個 helper 去抬全專案 lib 不划算。
 */
const firstSuccess = <T>(ps: Promise<T>[]): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    let pending = ps.length
    if (pending === 0) {
      reject(new Error('no candidate'))
      return
    }
    for (const p of ps) {
      p.then(resolve, () => {
        if (--pending === 0) reject(new Error('all candidates failed'))
      })
    }
  })

/**
 * 從候選 array 中選一個「先回應」的。
 *
 * - 候選 ≤ 1 → 直接回 `[0]`，**完全不發請求**（沒得比）。
 * - 全部失敗 / 逾時 → fallback `[0]`（= race 前的行為，不會更差）。
 * - 不論輸贏都會 `abort()` 其餘請求，不留 pending。
 */
const raceCandidates = async (
  arr: string[] | undefined,
  probe: (base: string, signal: AbortSignal) => Promise<unknown>,
): Promise<string> => {
  const list = (arr ?? []).map(strip).filter(Boolean)
  const fallback = pick(arr)
  if (list.length <= 1) return fallback

  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), RACE_TIMEOUT_MS)
  try {
    return await firstSuccess(list.map((base) => probe(base, ctrl.signal).then(() => base)))
  } catch {
    return fallback // AggregateError（全掛）或 abort（逾時）
  } finally {
    clearTimeout(timer)
    ctrl.abort()
  }
}

/**
 * API 探針：`GET {base}/healthz`，**必須正好回 204** 才算贏（後端 2026-09-07 提供的
 * 免 token 輕量端點，status code 固定 204、無 body）。
 *
 * 🛑 不用 `res.ok`：域名被 ban 時，ISP / 中間盒常回一個 **HTTP 200 的攔截頁**，
 *    那樣 `ok === true` 但 API 根本不通 → 選中它 = 整個 app 壞掉。
 *    攔截頁不可能回 204，嚴格比對 204 就擋得住（也因此不必再解 body）。
 *
 * ℹ️ 歷史：原探針打 `/v1/{gameCode}/version` 並驗 `data.version`，後端把該端點改回
 *    **要 token**（版本角標改登入後才打，見 useVersionInfo）→ race 換 /healthz。
 */
const probeHealthz = async (base: string, signal: AbortSignal): Promise<void> => {
  const r = await fetch(`${base}/healthz`, { signal, cache: 'no-store' })
  if (r.status !== 204) throw new Error(`HTTP ${r.status}`)
}

/** race API 候選，贏家寫回 `API_BASE`。圖片的 race 見 `useGameImages.raceImageBase`。 */
const raceBases = async (cfg: AppConfig | undefined): Promise<void> => {
  API_BASE = await raceCandidates((cfg ?? {}).api, probeHealthz)
}

/**
 * 圖片候選 array（原值，未 strip）。給 `useGameImages` 的 race 用。
 *
 * 🛑 **圖片的 race 不在開機做** —— 見 `IMAGES_BASE` 的註解。
 */
export let IMAGES_CANDIDATES: readonly string[] = []

/** 圖片 race 的贏家寫回 `IMAGES_BASE`（由 `useGameImages` 呼叫）。 */
export const setImagesBase = (base: string): void => {
  const v = strip(base)
  if (v) IMAGES_BASE = v
}

/**
 * 執行環境標記（取代舊 `import.meta.env.VITE_MODE`）。由 `config.json.env` 提供（runtime）。
 * 缺值 → 空字串（視為非 local；線上漏生成 config = env 空，與 base 空同樣屬「壞掉」情境）。
 */
export let APP_ENV: string = ''

/**
 * acc dev 登录（URL 帶 `?acc=`）前端啟用旗標。取代舊 build-time `__ENABLED_DEV_LOGIN__`
 * → runtime（不再 Rollup DCE；acc 分支常駐 bundle）。
 *
 * ⚠️ **前端不再用 env 擋（2026-07 改版）**：dev login 端點 `POST /v1/users/dev/login` 各環境
 *    是否開放，**由後端自己 gate**（前端全環境放行）。理由：dev-login 端點可能全環境都掛，
 *    「哪些環境能用」是後端 policy，前端 env 閘只會造成前後端不同步。真正的安全邊界在後端端點
 *    ——線上不開該端點，前端帶 `?acc=` 也換不到 token。
 *    （URL 帶 `?acc=` 即走 devAcc，見 authService.shouldUseDevAccLogin。）
 *
 * （恆 true、不吃 config → 維持 `const`。）
 */
export const ENABLED_DEV_LOGIN: boolean = true

/**
 * 訪客（guest）登入模式：`config.json.env === 'demo'`（demo 試玩部署，部署層級）。
 * 命中時 auth 一律走 `POST /v1/users/guest/login`（免 ticket / 免參數），token 只放記憶體不持久化。
 * 用 env（不另加 config 欄位、不走 query）：demo 是獨立部署、整包 guest-only，屬部署屬性。
 */
export let IS_GUEST_LOGIN: boolean = false

/** API base（auth + backend 同址，全專案 useHttp / game_frontend 共用）。 */
export let API_BASE: string = ''

/**
 * Cocos 遊戲資源域名前綴（native 遊戲拼 gameUrl 用）。
 *
 * 🛑 **不吃 config.json 的 gameH5**：遊戲與 host 同網域，故線上一律 = host 自己的 `window.location.origin`。
 *    只有本機 `pnpm dev`（`config.json.env === 'local'`）連不到本地遊戲 → 寫死 dev-h5。
 *    用 `APP_ENV` 判本機（同 DevSpinPanel），不 sniff hostname。
 */
const LOCAL_GAME_H5 = 'https://dev-h5.au-slot.net'
export let GAME_H5_BASE: string = ''

/**
 * External Modules base（SET_CONFIG 下發給 Cocos）—— 與遊戲同網域下的 `/ext-module`。
 * 用 GAME_H5_BASE 拼，故本機也自動指到 dev-h5（而非 localhost）。
 */
export let EXTERNAL_MODULES_BASE: string = ''

/**
 * 圖片 CDN 前綴（`buildImageURL` 用）。預設 = `config.json.images[0]`，
 * 之後由 `useGameImages` 的 race 覆寫（`setImagesBase`）。
 *
 * 🛑 **圖片 race 刻意不在開機做**：開機時**還不知道任何圖片路徑**（路徑要等
 *    `/v1/images/external`），只能探根目錄；而探根目錄只證明「有東西回應」——
 *    域名被 ban 時 ISP 常回 **HTTP 200 的攔截頁**，那樣會選中一台完全拿不到圖的主機。
 *    所以改成**拿到清單後、用真的一張圖去 race**（連 CORS + content-type 一起驗），
 *    而且那次請求的 blob 直接留用，不浪費。見 `useGameImages.raceImageBase`。
 */
export let IMAGES_BASE: string = ''

/**
 * 新 loading 流程的 **kill switch**（`config.json.loadingStartButton`，docs/LOADING_PROTOCOL.md〈五〉）。
 *   - `true`（預設、缺值同義）：新款（GAME_READY 帶 hostLoading）LOAD_COMPLETE 後**換「開始遊戲」鈕**，
 *     玩家按下才送 LOADING_CLOSED。
 *   - `false`：退化成「收到 LOAD_COMPLETE **立刻自動**送 LOADING_CLOSED、不顯示開始鈕」。
 * 線上出事時改一個 JSON 就退場，不用重 build host、更不用重 build 遊戲。
 * 只影響新款；舊款（沒旗標）本來就不走這段。
 */
export let LOADING_START_BUTTON: boolean = true

/** 依 config 物件推導出上面所有值。冪等，可重複呼叫。 */
const applyRuntimeConfig = (cfg: AppConfig | undefined): void => {
  const c = cfg ?? {}
  APP_ENV = (c.env ?? '').trim()
  IS_GUEST_LOGIN = APP_ENV === AppEnv.Demo
  API_BASE = pick(c.api)
  IMAGES_BASE = pick(c.images)
  IMAGES_CANDIDATES = (c.images ?? []).map(strip).filter(Boolean)
  const isLocal = APP_ENV === AppEnv.Local
  GAME_H5_BASE = strip(
    isLocal ? LOCAL_GAME_H5 : typeof window !== 'undefined' ? window.location.origin : '',
  )
  EXTERNAL_MODULES_BASE = `${GAME_H5_BASE}/ext-module`
  // 缺值 = 開（只有明確 false 才關）；字串 'false' 也當關，防運維手改 JSON 打成字串
  LOADING_START_BUTTON = !(
    c.loadingStartButton === false || (c.loadingStartButton as unknown) === 'false'
  )
}

/** index.html 沒跑到（理論上不會）時的補救 fetch，帶同樣的 8s timeout。 */
const fetchConfigFallback = async (): Promise<AppConfig | undefined> => {
  try {
    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), 8000)
    try {
      const r = await fetch('/config.json', { signal: ctrl.signal })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return (await r.json()) as AppConfig
    } finally {
      clearTimeout(timer)
    }
  } catch (e) {
    console.warn('[config] fallback fetch /config.json failed', e)
    return undefined
  }
}

/**
 * 套用 runtime config。**必須在 `createApp().mount()` 之前 await**（見 main.ts）。
 *
 * 正常路徑：index.html 的 inline script 已經把 fetch 發出去 → 這裡只是等它落地。
 * 失敗（含 timeout）時 inline script 自己 catch 過，這裡拿到 undefined → 各 base 為空字串，
 * 維持既有「無 fallback、config 壞掉 = app 壞掉」的極簡取捨（不另外做降級）。
 */
export const initRuntimeConfig = async (): Promise<void> => {
  if (typeof window === 'undefined') {
    applyRuntimeConfig(undefined)
    return
  }
  if (window.__APP_CONFIG_PROMISE__) {
    await window.__APP_CONFIG_PROMISE__
  } else if (!window.__APP_CONFIG__) {
    window.__APP_CONFIG__ = await fetchConfigFallback()
  }
  applyRuntimeConfig(window.__APP_CONFIG__)
  // 候選 domain race（見檔頭）。先 apply 再 race → race 全掛時本來就是 [0]，不會有空窗。
  await raceBases(window.__APP_CONFIG__)
}
